import { useQuery } from '@tanstack/react-query';

import { FBMOrderIngestResultsEndpoint } from 'api/fbmOrderIngestResultsEndpoint';
import { ApiError } from 'shared/errors/ApiError';

const fbmOrderIngestResultsEndpoint = new FBMOrderIngestResultsEndpoint();

export const FBM_INGEST_RESULTS_QUERY_KEY = 'fbmIngestResults';

/**
 * Query key factory for FBM order ingest results (paginated list).
 */
export const fbmIngestResultsQueryKeys = {
    all: [FBM_INGEST_RESULTS_QUERY_KEY],
    list: (fbmAccountName, page, pageSize, q = undefined, types = undefined) => [
        FBM_INGEST_RESULTS_QUERY_KEY,
        fbmAccountName,
        page,
        pageSize,
        q,
        types,
    ],
};

/**
 * Fetches paginated FBM order ingest results for an account.
 *
 * @param {Object} options
 * @param {string} options.fbmAccountName - FBM account name (from user context)
 * @param {number} options.page - Zero-based page index
 * @param {number} options.pageSize - Page size
 * @param {string} [options.q = undefined] - Search query
 * @param {string[]} [options.types = undefined] - Ingest result types to filter by
 * @param {boolean} [options.enabled=true] - Whether the query should run
 * @returns {import('@tanstack/react-query').UseQueryResult<
 *   { ingestResults: Array<Record<string, unknown>>, page: { currentPage: number, pageSize: number, nextPage: number | null } },
 *   ApiError
 * >}
 */
export function useFbmIngestResultsQuery({
    enabled = true,
    fbmAccountName,
    page,
    pageSize,
    q = undefined,
    types = undefined,
}) {
    return useQuery({
        enabled: enabled && !!fbmAccountName,
        placeholderData: (previousData) => previousData,
        queryFn: async () => {
            const response = await fbmOrderIngestResultsEndpoint.getOrderIngestResults(
                fbmAccountName,
                page,
                pageSize,
                q,
                types,
            );
            if (!response?.status) {
                throw new ApiError(
                    response?.msg ?? 'Failed to fetch FBM ingest results',
                    response?.statusCode,
                    response?.data,
                );
            }
            return {
                ingestResults: response.data?.ingestResults ?? [],
                page: response.data?.page ?? {
                    currentPage: page,
                    nextPage: null,
                    pageSize,
                },
            };
        },
        queryKey: fbmIngestResultsQueryKeys.list(fbmAccountName, page, pageSize, q, types),
        retry: (failureCount, err) => {
            if (err instanceof ApiError && err.isServerError) {
                return false;
            }
            if (err instanceof ApiError && err.isAuthError) {
                return false;
            }
            return failureCount < 2;
        },
        staleTime: 0,
    });
}
